import React, {useState} from "react";
import PropTypes from "prop-types";
import {Col, FormGroup} from "reactstrap";
import {LuUpload, LuFile, LuX} from "react-icons/lu";
import AsteriskIcon from "../../asteriskIcon";

export const UploaderLabel = ({htmlFor, name, hint, isRequired}) => {
  if (!name && !hint) return null;
  return (
    <div style={{marginBottom: "10px"}}>
      {name && (
        <div style={{display: "flex", alignItems: "center", gap: "3px"}}>
          <label
            htmlFor={htmlFor}
            style={{
              fontFamily: "'Roboto', sans-serif",
              fontSize: "13px",
              fontWeight: 600,
              color: "#111827",
              margin: 0,
              cursor: "pointer",
              lineHeight: 1.4,
            }}
          >
            {name}
          </label>
          {isRequired && <AsteriskIcon />}
        </div>
      )}
      {hint && (
        <div style={{marginTop: "3px"}}>
          {(Array.isArray(hint) ? hint : [hint]).map((line, i) => (
            <p
              key={i}
              style={{
                fontFamily: "'Roboto', sans-serif",
                fontSize: "12px",
                color: "#9ca3af",
                lineHeight: 1.55,
                margin: 0,
              }}
            >
              {line}
            </p>
          ))}
        </div>
      )}
    </div>
  );
};

UploaderLabel.propTypes = {
  htmlFor: PropTypes.string,
  name: PropTypes.string,
  hint: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  isRequired: PropTypes.bool,
};

const ImageUploadCreator = ({
  label,
  help_text,
  name,
  accept = "image/*",
  colSize = "6",
  large = false,
  isRequired = false,
  value = null,
  file = null,
  fileType,
  inline = false,
  onChange = () => {},
}) => {
  const [showPreview, setShowPreview] = useState(false);
  const [isRemoved, setIsRemoved] = useState(false);
  const [selectedFileType, setSelectedFileType] = useState("");
  const [isHovered, setIsHovered] = useState(false);

  const displayImage = file ? URL.createObjectURL(file) : (!isRemoved && value) || null;

  const displayName = file
    ? file.name
    : !isRemoved && value
      ? value.split("/").pop()
      : null;

  const displayType = selectedFileType || fileType;
  const isVideo = displayType && displayType.includes("video");

  const handleChange = (e) => {
    const picked = e.target.files?.[0];
    if (!picked) return;
    setShowPreview(false);
    setIsRemoved(false);
    setSelectedFileType(picked.type);
    onChange({target: {file: picked, name}});
    e.target.value = "";
  };

  const handleRemove = () => {
    setShowPreview(false);
    setIsRemoved(true);
    onChange({target: {file: null, name}});
  };

  const truncatedName =
    displayName && displayName.length > 30 ? displayName.slice(0, 27) + "…" : displayName;

  const labelContent = (label || help_text) && (
    <UploaderLabel htmlFor={name} name={label} hint={help_text} isRequired={isRequired} />
  );

  const controlContent = (
    <div style={{width: "100%"}}>
      {/* Upload zone */}
      <label
        htmlFor={name}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          display: "block",
          border: isHovered ? "1.5px dashed #1a9fd4" : "1.5px dashed #e8eaee",
          borderRadius: "10px",
          background: isHovered ? "rgba(26, 159, 212, 0.03)" : "#f4f6f9",
          padding: "20px",
          textAlign: "center",
          cursor: "pointer",
          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            width: "34px",
            height: "34px",
            background: "#ffffff",
            border: "1px solid #e8eaee",
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 12px",
          }}
        >
          <LuUpload size={16} color="#6b7280" />
        </div>
        <p
          style={{
            fontFamily: "'Roboto', sans-serif",
            fontSize: "13px",
            fontWeight: 500,
            color: "#111827",
            margin: "0 0 4px",
          }}
        >
          {displayName ? "Replace file" : "Upload file"}
        </p>
        <p
          style={{
            fontFamily: "'Roboto', sans-serif",
            fontSize: "11px",
            color: "#9ca3af",
            margin: 0,
            lineHeight: 1.5,
          }}
        >
          Click to browse files
        </p>
      </label>

      <input
        id={name}
        type="file"
        accept={accept}
        style={{display: "none"}}
        onChange={handleChange}
      />

      {/* File pill */}
      {displayName && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            padding: "10px 14px",
            background: "#f4f6f9",
            border: "1px solid #e8eaee",
            borderRadius: "10px",
            marginTop: "12px",
          }}
        >
          <LuFile size={16} color="#1a9fd4" style={{flexShrink: 0}} aria-hidden="true" />
          <span
            style={{
              flex: 1,
              fontFamily: "'Roboto', sans-serif",
              fontSize: "12px",
              color: "#111827",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {truncatedName}
          </span>
          <button
            type="button"
            onClick={() => setShowPreview((p) => !p)}
            style={{
              fontFamily: "'Roboto', sans-serif",
              fontSize: "12px",
              fontWeight: 500,
              color: "#1a9fd4",
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 0,
              flexShrink: 0,
              transition: "opacity 0.2s",
            }}
            onMouseEnter={(e) => (e.target.style.opacity = "0.7")}
            onMouseLeave={(e) => (e.target.style.opacity = "1")}
          >
            {showPreview ? "Hide" : "Preview"}
          </button>
          <button
            type="button"
            onClick={handleRemove}
            title="Remove file"
            aria-label="Remove file"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "18px",
              height: "18px",
              color: "#9ca3af",
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 0,
              flexShrink: 0,
              transition: "color 0.2s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "#ef4444")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "#9ca3af")}
          >
            <LuX size={14} aria-hidden="true" />
          </button>
        </div>
      )}

      {/* Preview panel */}
      {showPreview && displayImage && (
        <div
          style={{
            marginTop: "12px",
            border: "1px solid #e8eaee",
            borderRadius: "10px",
            overflow: "hidden",
            background: "#f9f9f9",
          }}
        >
          {isVideo ? (
            // eslint-disable-next-line jsx-a11y/media-has-caption
            <video
              src={displayImage}
              controls
              style={{width: "100%", height: "auto", display: "block"}}
            />
          ) : (
            <img
              src={displayImage}
              alt={label || "Preview"}
              style={{width: "100%", height: "auto", display: "block"}}
            />
          )}
        </div>
      )}
    </div>
  );

  // inline mode: bare control only (parent row owns the label)
  if (inline) return controlContent;

  return (
    <Col md={large ? "12" : colSize}>
      <FormGroup row>
        <Col md={12}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "190px 1fr",
              gap: "28px",
              width: "100%",
            }}
          >
            <div>{labelContent}</div>
            <div>{controlContent}</div>
          </div>
        </Col>
      </FormGroup>
    </Col>
  );
};

ImageUploadCreator.propTypes = {
  label: PropTypes.string,
  help_text: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  name: PropTypes.string.isRequired,
  accept: PropTypes.string,
  colSize: PropTypes.string,
  large: PropTypes.bool,
  isRequired: PropTypes.bool,
  maxWidth: PropTypes.string,
  value: PropTypes.string,
  file: PropTypes.object,
  fileType: PropTypes.string,
  inline: PropTypes.bool,
  onChange: PropTypes.func,
};

export default ImageUploadCreator;
